import { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import toast from 'react-hot-toast';
import { getUser } from 'utils/auth';
import { canViewRos, canEditRos, canSendRos } from 'utils/rosPermissions';
import {
  ReportOfSaleVersion,
  RosReportType,
  useSetActiveReportOfSaleVersionMutation,
} from '../../../../services/api/report-of-sale';
import EditReportOfSaleModal from './EditReportOfSaleModal';
import SendReportOfSaleModal from './SendReportOfSaleModal';
import Tooltip from 'components/Tooltip';
import CarretUpIcon from 'components/Icons/carret_up.svg';
import CarretDownIcon from 'components/Icons/carret_down.svg';
import EyeIcon from 'components/Icons/eye_active.svg';
import SendEmailIcon from 'components/Icons/send_email.svg';

const Wrapper = styled.div<{ $infoBlock?: boolean }>`
  background: ${({ $infoBlock }) => ($infoBlock ? 'rgba(249, 249, 249, 1)' : '#f7f8fa')};
  border-radius: ${({ $infoBlock }) => ($infoBlock ? '30px' : '8px')};
  padding: 12px 16px;
  margin-bottom: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding-right: 10px;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  color: #005a96;
`;

const Body = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '500px' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  margin-top: ${({ $open }) => ($open ? '8px' : '0')};
  transition: max-height 0.15s ease, opacity 0.12s ease, margin-top 0.15s ease;
`;

const ScrollArea = styled.div`
  max-height: 102px;
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 8px;
    min-height: 49px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #cccccc66;
  &:last-child {
    border-bottom: none;
  }
  font-size: 14px;
  font-weight: 400;
  color: #666666;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const ViewLink = styled.span`
  color: #225a91;
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
`;

const Empty = styled.div`
  padding: 8px 0;
  color: #666666;
  font-size: 14px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EditLink = styled.span`
  color: #225a91;
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
`;

const SendIcon = styled(SendEmailIcon)`
  width: 20px;
  height: 20px;
  color: #cccccc;
  cursor: pointer;
  flex-shrink: 0;
  display: block;
  &:hover {
    color: #3b92cc;
  }
`;

const ActiveEye = styled(EyeIcon)`
  width: 20px;
  height: 20px;
  color: #225a91;
  cursor: default;
  flex-shrink: 0;
  display: block;
`;

const InactiveEye = styled(EyeIcon)`
  width: 20px;
  height: 20px;
  color: #999999;
  cursor: pointer;
  flex-shrink: 0;
  display: block;
  &:hover {
    color: #225a91;
  }
`;

type Props = {
  vehicleId: number;
  reportType: RosReportType;
  title: string;
  versions: ReportOfSaleVersion[];
  infoBlock?: boolean;
};

const ReportOfSaleSection = ({ vehicleId, reportType, title, versions, infoBlock }: Props) => {
  const [open, setOpen] = useState(true);
  const [editingVersion, setEditingVersion] = useState<ReportOfSaleVersion | null>(null);
  const [sendingVersion, setSendingVersion] = useState<ReportOfSaleVersion | null>(null);
  const user = getUser();
  const canView = canViewRos(user);
  const canEdit = canEditRos(user);
  const canSend = canSendRos(user);
  const [setActive] = useSetActiveReportOfSaleVersionMutation();

  const activateVersion = async (id: number) => {
    try {
      await setActive({ vehicleId, reportType, id }).unwrap();
    } catch {
      toast.error('Failed to set active version.');
    }
  };

  return (
    <Wrapper $infoBlock={infoBlock}>
      <Header onClick={() => setOpen(prev => !prev)}>
        <Title>{title}</Title>
        <HeaderRight>{open ? <CarretUpIcon /> : <CarretDownIcon />}</HeaderRight>
      </Header>

      <Body $open={open}>
        {versions.length === 0 ? (
          <Empty>No versions yet.</Empty>
        ) : (
          <ScrollArea>
            {versions.map(v => (
              <Row key={`${title}-${v.version}`}>
                <span>
                  {v.version} {v.action} by {v.actor} on{' '}
                  {moment(v.created_at).format('MM/DD/YY, hh:mm A')}
                </span>
                <RowActions>
                  {canSend && (
                    <Tooltip title="Send" light>
                      <SendIcon aria-label="Send" onClick={() => setSendingVersion(v)} />
                    </Tooltip>
                  )}
                  {canView &&
                    (v.is_active ? (
                      <Tooltip title="Show" light>
                        <ActiveEye aria-label="Active version" />
                      </Tooltip>
                    ) : (
                      canEdit && (
                        <Tooltip title="Show" light>
                          <InactiveEye
                            role="button"
                            aria-label="Make active"
                            onClick={() => activateVersion(v.id)}
                          />
                        </Tooltip>
                      )
                    ))}
                  {canEdit && <EditLink onClick={() => setEditingVersion(v)}>Edit</EditLink>}
                  {canView && (
                    <ViewLink onClick={() => window.open(v.url, '_blank', 'noopener,noreferrer')}>
                      View
                    </ViewLink>
                  )}
                </RowActions>
              </Row>
            ))}
          </ScrollArea>
        )}
      </Body>

      {editingVersion && (
        <EditReportOfSaleModal
          vehicleId={vehicleId}
          reportType={reportType}
          versionId={editingVersion.id}
          onClose={() => setEditingVersion(null)}
        />
      )}

      {sendingVersion && (
        <SendReportOfSaleModal
          vehicleId={vehicleId}
          reportType={reportType}
          versionId={sendingVersion.id}
          onClose={() => setSendingVersion(null)}
        />
      )}
    </Wrapper>
  );
};

export default ReportOfSaleSection;
